import styles from "./Tab.module.scss";
import AvatarWithMarketplace from '@components/AvatarWithMarketplace';
import Box from '@components/Box';
import cn from 'classnames';
import DataTable from '@containers/DataTable';
import InfoIcon from '@assets/icons/info-grey-18.svg';
import ProgressBar from '@components/ProgressBar';
import {
  createDimensionsStr,
  formatDate,
  formatNumber,
} from '@utils/formatValues';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { Filters, FilterTypes } from '@containers/Filters';
import { getFilterNameByCode, isErrorState } from '@containers/Filter/ParcelStatusFilter';
import { getParcels, ParcelPageItem } from '@api/apiClient';
import { getProgressByTrackEventCode, normalizeTrackEventType } from '@utils/trackEvents';
import { Row, Column } from '@components/Grid';
import { TableColumn, TableColumnType, TableRowType } from '@components/Table/TableTypes';
import { Text, TextColor, TextSize, TextType } from '@components/Text';
import { Tooltip } from '@components/new/Tooltip';
import TrackingNumber from "@components/TrackingNumber";
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { INewTabProps } from '@src/pages/ParcelsPage/types';

const NewTab = (props: INewTabProps) => {
  const {
    handlerSetExcelExportFilters,
    handlerTableDataUpdate,
    isFilterOpen,
    onOpenParcel,
    pending,
    sellersNotFound,
    setIsFilterOpen
  } = props;

  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [innerFilters, setInnerFilters] = useState<FilterModel[]>([]);

  const { t } = useTranslation();

  const columns: TableColumn<ParcelPageItem>[] = [
    {
      header: t("common.trackingNumber"),
      accessor: (row: TableRowType<ParcelPageItem>) => (
        <TrackingNumber
          externalId={row.data?.externalNumber}
          trackNumber={row.data?.trackNumber}
          onClick={() => onOpenParcel(row.data?.id)}
        />
      ),
      fixWidth: true,
      noOverflow: true,
      width: "200px"
    },
    {
      header: t("parcelsPage.newTable.service"),
      accessor: (row: TableRowType<ParcelPageItem>) => row.data.serviceName,
      width: "200px"
    },
    {
      header: t("parcelsPage.newTable.step"),
      accessor: (row: TableRowType<ParcelPageItem>) => {
        const filterName = getFilterNameByCode(row.data.actualTrackEventTypeValue, row.data.cancellationReasonType)
        const isError = isErrorState(filterName);
        const normalizedType = row.data.actualTrackEventTypeValue
          ? normalizeTrackEventType(row.data.actualTrackEventTypeValue)
          : 0;
        // Для обратного потока показываем этап конкретного события (как в карточке посылки),
        // а не группу фильтра: группа «Начат возврат» покрывает и более поздние статусы.
        const isReturnFlow = normalizedType === 6000 || normalizedType === 6001 || normalizedType >= 30000;
        const groupLabel = t(`filters.parcelStatusFilter.${filterName}`, { defaultValue: "" });
        const eventLabel = t(`trackEventTypes.${normalizedType || "0"}`, { defaultValue: "" });
        const label = (isReturnFlow
          ? (eventLabel || groupLabel)
          : (groupLabel || eventLabel))
          || row.data.actualTrackEventType || "";
        return (
          <>
            <ProgressBar
              progress={getProgressByTrackEventCode(row.data.actualTrackEventTypeValue)}
              className={styles.progress}
              cells
            />
            <Text
              color={isError ? TextColor.ERROR : TextColor.DARK_MUTED}
              type={TextType.SPAN}
              size={TextSize.S}
              className={styles.progressText}
            >
              {label}
            </Text>
          </>
        );
      },
      type: TableColumnType.SECONDARY,
      width: "245px",
    },
    {
      header: t('parcelsPage.newTable.dimensions'),
      accessor: (row: TableRowType<ParcelPageItem>) => (
          <Text
              color={TextColor.PRIMARY}
              size={TextSize.S}
          >
            {createDimensionsStr(row.data.actualDimensions)}
          </Text>
      )
    },
    {
      header: <div className={styles.title}>
        {t('parcelsPage.newTable.weight')}
        <Tooltip
            action={
              <img
                  alt="Info"
                  src={InfoIcon}
              />
            }
            left={8}
            text={t('parcelsPage.newTable.tooltipWeight')}
            top={24}
            whiteSpace="pre-wrap"
        />
      </div>,
      accessor: (row: TableRowType<ParcelPageItem>) => (
          <Text
              color={TextColor.PRIMARY}
              size={TextSize.S}
          >
            {row.data.actualWeight ? formatNumber(row.data.actualWeight.value) : '—'}
          </Text>
      ),
      noOverflow: true
    },
    {
      header: t("parcelsPage.newTable.seller"),
      accessor: (row: TableRowType<ParcelPageItem>) => (
        <AvatarWithMarketplace
          id={row.data.sellerId}
          name={row.data.sellerName}
          marketplace={row.data.marketplaceType}
        />
      ),
      alignment: 'center',
      noOverflow: true
    },
    {
      header: t("parcelsPage.newTable.date"),
      accessor: (row: TableRowType<ParcelPageItem>) => formatDate(row.data.createdAt, "dd.MM.yy HH:mm"),
      alignment: 'right',
      type: TableColumnType.TERTIARY
    },
  ];

  return (
    <div>
      <Box pb={5} className={cn(!isFilterOpen && styles.hidden)}>
        <Column phone={12}>
          <Row justify="right" gap={5}>
            <Filters
              id="parcels-new"
              openFilters={() => setIsFilterOpen(true)}
              items={sellersNotFound ? [] : [
                {
                  name: "trackOrExternalNumbers",
                  type: FilterTypes.MULTI_SEARCH,
                  placeholder: t("parcelsPage.trackNumberSearch"),
                },
                {
                  name: "serviceIds",
                  type: FilterTypes.SERVICE_PARCELS,
                },
                {
                  name: "sellerIds",
                  type: FilterTypes.SELLER,
                },
                {
                  name: "marketplaceType",
                  type: FilterTypes.MARKETPLACE,
                },
                {
                  name: "dateRange",
                  type: FilterTypes.DATE_RANGE,
                  placeholder: t("filters.createAtParcel"),
                  fieldNames: ["startDate", "endDate"],
                },
                {
                  name: "trackEventTypes",
                  type: FilterTypes.PARCEL_STATUS,
                },
              ]}
              onChange={(v, isReady) => {
                if (isReady) {
                  setIsInitialized(true);
                }
                handlerSetExcelExportFilters(v);
                setInnerFilters(v)
              }}
              values={innerFilters}
              asPanel
            />
          </Row>
        </Column>
      </Box>
      <DataTable<ParcelPageItem>
        onUpdate={handlerTableDataUpdate}
        queryKey="parcels-page"
        pending={pending || !isInitialized}
        getData={getParcels}
        columns={columns}
        notFound={t("parcelsPage.notParcels")}
        filters={innerFilters}
      />
    </div>
  );
};

export default NewTab;
